import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Shift } from "../../components/shiftcard/ShiftCard";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { fetchAvailableShifts, AvailableShiftsFilter } from "../../redux/AvailableShiftsReducer/availableShiftsSlice";
import { grabShift, GrabShiftReasonCode, resetGrabShift } from "../../redux/GrabShiftReducer/grabShiftSlice";
import { ActivityLogEventActions } from "../../constants/activityLogEventActions";
import ShiftPageContainer from "../../components/shift-page-container/ShiftPageContainer";

const filterMap: Record<number, AvailableShiftsFilter> = {
    0: 'today',
    1: 'all',
    2: 'thisweek',
};

const conflictMessageMap: Partial<Record<GrabShiftReasonCode, string>> = {
    DUPLICATE_CLAIM_BY_USER: "You have already claimed a slot for this shift.",
    SHIFT_ALREADY_FILLED: "This shift was just claimed by another user. Refreshing available shifts.",
    SHIFT_EXPIRED_OR_CANCELED: "This shift is no longer active. Refreshing available shifts.",
    USER_NOT_ELIGIBLE: "You are no longer eligible to claim this shift.",
    SHIFT_NOT_FOUND: "This shift was not found. Please refresh and try again.",
    CONFLICT_UNKNOWN: "This shift changed while you were viewing it. Refreshing available shifts.",
};

const shouldRefreshOnError = (reasonCode: GrabShiftReasonCode | null, fallbackMessage: string): boolean => {
    if (reasonCode === 'SHIFT_ALREADY_FILLED' || reasonCode === 'SHIFT_EXPIRED_OR_CANCELED' || reasonCode === 'CONFLICT_UNKNOWN') {
        return true;
    }

    const normalized = fallbackMessage.toLowerCase();
    return normalized.includes('no longer available') || normalized.includes('already filled') || normalized.includes('expired');
};

const GrabShiftPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.currentUser);
    const { shifts, loading } = useAppSelector((state) => state.availableShifts);
    const { success: grabSuccess, successMessage: grabSuccessMessage, isDuplicate, reasonCode, error: grabError } = useAppSelector((state) => state.grabShift);

    const [selectedTab, setSelectedTab] = useState(0);
    const [grabShiftTabs] = useState([
        { label: 'Today', content: '' },
        { label: 'All Shifts', content: '' },
        { label: 'This Week', content: '' }
    ]);

    const loadShiftsForTab = useCallback((tabIndex: number) => {
        if (user?.siteCd) {
            const filter = filterMap[tabIndex];
            dispatch(fetchAvailableShifts({
                siteCd: user.siteCd,
                filter,
                userId: user.userId,
            }));
        }
    }, [dispatch, user?.siteCd, user?.userId]);

    const handleTabChange = (selectedTab: number) => {
        setSelectedTab(selectedTab);
        loadShiftsForTab(selectedTab);
    };

    const handleGrabConfirm = (shift: Shift, action: "grab" | "approve" | "reject") => {
        if (action === "grab") {
            const payload = {
                requestShiftId: shift.id,
                grabById: user.userId,
                grabByName: user.firstName + " " + user.lastName,
                requestSite: user.siteCd,
                eventAction: [ActivityLogEventActions.ClaimedShift]
            };
            dispatch(grabShift({ payload }));
        }
    };

    useEffect(() => {
        loadShiftsForTab(0);
    }, [loadShiftsForTab]);

    useEffect(() => {
        if (grabSuccess) {
            toast.success(grabSuccessMessage ?? "Shift grabbed successfully!", { duration: 6000, });
            loadShiftsForTab(selectedTab);
            dispatch(resetGrabShift());
        }
        if (grabError) {
            if (reasonCode && conflictMessageMap[reasonCode]) {
                const message = conflictMessageMap[reasonCode] ?? grabError;
                const isUserWarning = reasonCode === 'DUPLICATE_CLAIM_BY_USER' || reasonCode === 'SHIFT_ALREADY_FILLED' || reasonCode === 'SHIFT_EXPIRED_OR_CANCELED' || reasonCode === 'CONFLICT_UNKNOWN';

                if (isUserWarning) {
                    toast.warning(message);
                } else {
                    toast.error(message);
                }
            } else if (isDuplicate) {
                toast.warning("You have already claimed a slot for this shift.");
            } else {
                toast.error(grabError);
            }

            if (shouldRefreshOnError(reasonCode, grabError)) {
                loadShiftsForTab(selectedTab);
            }

            dispatch(resetGrabShift());
        }
    }, [grabSuccess, grabSuccessMessage, grabError, isDuplicate, reasonCode, selectedTab, dispatch, loadShiftsForTab]);

    return (
        <ShiftPageContainer
            title="Available Shifts"
            subtitle="Find and grab shifts that work for your schedule"
            tabs={grabShiftTabs}
            selectedTab={selectedTab}
            onTabChange={handleTabChange}
            shifts={shifts}
            loading={loading}
            noShiftsMessage="No shifts available"
            onShiftAction={handleGrabConfirm}
        />
    );
};

export default GrabShiftPage;